import { chain, isEqual } from 'lodash';
import dayjs from 'dayjs';
import convert from 'convert';
import {
  AdubacaoRow,
  ItemSaldo,
  RecomendacaoSemeadura,
  RecomendacaoSemeaduraFormValues,
  TalhaoRow,
  Atividade,
  RecomendacaoSemeaduraSituacao,
  Safra,
} from '../../@types';
import {
  RecomendacaoSemeaduraCultivarToSave,
  RecomendacaoSemeaduraToSave,
} from '../../services/recomendacoes-semeadura';
import { getDataEstimadaParaAplicar } from './create-edit/talhoes/utils';
import {
  AtividadesService,
  ItensSaldoService,
  PropriedadesService,
  RecomendacaoSemeaduraService,
  TalhaoError,
} from '../../services';
import convertUnidade from '../../utils/convert-unidade';
import Agro1Toast from '../../components/Agro1Toast';

export const convertAdubos = async (
  baseObject: RecomendacaoSemeadura,
  adubosList?: ItemSaldo[],
) => {
  try {
    let allAdubos = adubosList;

    if (!allAdubos) {
      allAdubos = await ItensSaldoService.saldos({
        propriedadeId: baseObject.propriedade.id,
        operacaoItem: 'adubacao',
        noPaginate: true,
      });
    }

    return baseObject.adubos.map<AdubacaoRow>((novoAdubo) => {
      const novoTalhao =
        baseObject.glebas.find(({ id }) => id === novoAdubo.glebaId) || null;

      const itemEstoque =
        allAdubos!.find(({ id }) =>
          isEqual(id, novoAdubo.itemEstoque.itemId),
        ) || null;

      const identification = `${novoTalhao?.id}-${itemEstoque?.id}`;

      const gleba = {
        id: identification,
        talhao: novoTalhao,
        area: novoAdubo.area,
      };

      return {
        identification,
        talhao: gleba,
        adubo: itemEstoque,
        area: novoAdubo.area,
        dose: novoAdubo.dose ?? null,
        unidade: convertUnidade(novoAdubo.unidadeInsumo),
      };
    });
  } catch {
    throw new Error('erro ao converter adubos.');
  }
};

export const convertCultivares = async (
  baseObject: RecomendacaoSemeadura,
  cultivaresList?: ItemSaldo[],
) => {
  try {
    let allCultivares = cultivaresList;

    if (!allCultivares) {
      const cultivarIds = chain(baseObject.cultivares)
        .map((cultivar) => cultivar.itemEstoque.itemId)
        .uniq()
        .value();

      allCultivares = cultivarIds.length
        ? await ItensSaldoService.saldos({
            propriedadeId: baseObject.propriedade.id,
            operacaoItem: 'cultivar',
            item: cultivarIds.join(','),
          })
        : [];
    }

    let convertTalhaoErrorCount = 0;
    let convertSaldoErrorCount = 0;

    const convertedCultivares = baseObject.cultivares.map<TalhaoRow>(
      (cultivar) => {
        const novoTalhao = baseObject.glebas?.find(
          ({ id }) => id === cultivar.glebaId,
        );

        const saldo = allCultivares!.find(
          (item) =>
            item.tipo === cultivar.itemEstoque.tipoItem &&
            item.id === cultivar.itemEstoque.itemId,
        );

        if (!novoTalhao) convertTalhaoErrorCount++;
        if (!saldo) convertSaldoErrorCount++;

        const dataInicioTalhao = dayjs(cultivar.dataInicio).toDate();

        const dataTerminoMinimaTalhao = getDataEstimadaParaAplicar({
          dataBase: dataInicioTalhao,
          areaAplicada: cultivar.area,
          rendimentoOperacional:
            baseObject.parametros.rendimentoOperacionalMaximo || 0,
        });

        return {
          area: cultivar.area,
          talhao: novoTalhao ?? null,
          ciclo: cultivar.parametros.cicloDias ?? null,
          dataInicio: dataInicioTalhao,
          dataTermino: dayjs(cultivar.dataFim).toDate(),
          dataTerminoMinima: dataTerminoMinimaTalhao,
          densidade: cultivar.parametros.densidade ?? null,
          pg: cultivar.parametros.pg ?? null,
          pms: cultivar.parametros.pms ?? null,
          unidade: cultivar.unidadeInsumo
            ? convertUnidade(cultivar.unidadeInsumo)
            : null,
          itemSaldo: saldo ?? null,
          identification: `${novoTalhao?.id}-${cultivar.itemEstoque.itemId}`,
          dose: cultivar.dose ?? null,
        };
      },
    );

    if (convertTalhaoErrorCount) {
      Agro1Toast.show({
        detail:
          convertTalhaoErrorCount === 1
            ? 'Um talhão selecionado está inválido'
            : 'Alguns talhões selecionados estão inválidos',
        severity: 'error',
        sticky: true,
      });
    }

    if (convertSaldoErrorCount) {
      Agro1Toast.show({
        detail:
          convertSaldoErrorCount === 1
            ? 'Uma cultivar selecionada está inválida'
            : 'Algumas cultivares selecionadas estão inválidas',
        severity: 'error',
        sticky: true,
      });
    }

    return convertedCultivares;
  } catch (error) {
    console.error(error);
    throw new Error('erro ao converter cultivares dos talhões.');
  }
};

export const getPrintRequiredData = async (
  baseObject: RecomendacaoSemeadura,
  safras: Safra[],
): Promise<PropsGetRecomendacaoPrint> => {
  const { parametros } = baseObject;
  const adubos = await convertAdubos(baseObject);
  const cultivares = await convertCultivares(baseObject);

  return {
    adubos,
    talhoes: cultivares,
    comMargemSeguranca: parametros.calculoMargemSeguranca,
    sementeCobertura: parametros.cobertura,
    atividade: baseObject.atividade,
    distribuicaoRegulagemAdubo: parametros.distribuicaoRegulagemAbubos,
    distribuicaoRegulagemSemente: parametros.distribuicaoRegulagem,
    espacamento: parametros.espacamento,
    porcentagemAreaRemonte: parametros.areaRemontes,
    porcentagemCorrecaoDensidade: parametros.correcaoDensidade,
    porcentagemCorrecaoQuantidade: parametros.correcaoQuantidade,
    rendimentoOperacionalMaximo: parametros.rendimentoOperacionalMaximo,
    rendimentoOperacionalMinimo: parametros.rendimentoOperacionalMinimo,
    propriedadeDescricao: baseObject.propriedade.descricao,
    safraId: String(baseObject.safraId),
    safra: safras.find((safra) => safra.id === baseObject.safraId),
    id: baseObject.id,
    situacao: baseObject.situacao,
    status: baseObject.status,
    recomendante: baseObject.usuario.nomeCompleto,
    dataHoraImpressao: dayjs().format('DD/MM/YYYY HH:mm'),
  };
};

export const convertToSaveableObject = async (
  baseObject: RecomendacaoSemeadura,
): Promise<RecomendacaoSemeaduraToSave> => {
  const { parametros } = baseObject;
  const adubos = await convertAdubos(baseObject);
  const cultivares = await convertCultivares(baseObject);

  const adubosAttributes = getAduboAttributes({
    adubos,
    espacamento: parametros.espacamento,
    distribuicaoRegulagemAdubo: parametros.distribuicaoRegulagemAbubos,
    porcentagemCorrecaoQuantidade: parametros.correcaoQuantidade,
  } as PropsGetAduboAttributes);

  const cultivaresAttributes = getCultivaresAttributes({
    sementeCobertura: parametros.cobertura,
    talhoes: cultivares,
    distribuicaoRegulagemAdubo: parametros.distribuicaoRegulagemAbubos,
    espacamento: parametros.espacamento,
    porcentagemAreaRemonte: parametros.areaRemontes,
    porcentagemCorrecaoDensidade: parametros.correcaoDensidade,
    porcentagemCorrecaoQuantidade: parametros.correcaoQuantidade,
  } as PropsGetCultivaresAttributes);

  return {
    atividadeId: baseObject.atividade.id,
    propriedadeId: baseObject.propriedade.id,
    parametros,
    safraId: baseObject.safraId,
    situacao: baseObject.situacao,
    status: baseObject.status,
    adubosAttributes,
    cultivaresAttributes,
  };
};

export const getCultivaresAttributes = (
  formValue: RecomendacaoSemeaduraFormValues | PropsGetCultivaresAttributes,
  addItemInfo = false,
) =>
  formValue.talhoes.map<RecomendacaoSemeaduraCultivarToSave>((talhao) => {
    const fatorCorrecao = RecomendacaoSemeaduraService.getFatorCorrecao({
      porcentagemCorrecaoDensidade: formValue.porcentagemCorrecaoDensidade || 0,
      porcentagemAreaRemonte: formValue.porcentagemAreaRemonte || 0,
      porcentagemCorrecaoQuantidade:
        formValue.porcentagemCorrecaoQuantidade || 0,
    });

    const densidadeCorrigida =
      RecomendacaoSemeaduraService.getDensidadeCorrigida({
        fatorCorrecao,
        pg: talhao.pg || 0,
        densidade: talhao.densidade || 0,
      });

    let quantidade;

    if (formValue.sementeCobertura) {
      quantidade = talhao.dose || 0;
    } else {
      const isCulturaPorUnidade = AtividadesService.isMilho(
        formValue.atividade?.descricao,
      );

      quantidade = RecomendacaoSemeaduraService.getQuantidade({
        isCulturaPorUnidade,
        densidadeCorrigida,
        pms: talhao.pms || 0,
      }).resultado;
    }

    const numeroSementesParaRegulagem = convert(
      densidadeCorrigida *
        (formValue.espacamento || 0) *
        (formValue.distribuicaoRegulagemAdubo || 0),
      'm2',
    ).to('ha');

    return {
      insumoId: talhao.itemSaldo?.id ?? 0,
      insumoTipo: talhao.itemSaldo?.tipo ?? 0,
      area: talhao.area!,
      dataFim: talhao.dataTermino!,
      dataInicio: talhao.dataInicio!,
      dose: quantidade,
      glebaId: talhao.talhao!.id,
      unidadeId: talhao.unidade?.id ?? 1, // quando for cobertura, nao terá unidade e deve ser quilo fixo.
      parametros: {
        pms: talhao.pms ?? 0,
        pg: talhao.pg ?? 0,
        dose: talhao.dose ?? 0,
        densidade: talhao.densidade ?? 0,
        densidadeCorrigida,
        cicloDias: talhao.ciclo!,
        dataEstimadaColheita: dayjs(talhao.dataInicio)
          .add(talhao.ciclo!, 'days')
          .format('YYYY-MM-DD'),
        numeroSementesParaRegulagem,
        dataFimMin: talhao.dataTermino
          ? dayjs(talhao.dataTermino).format('YYYY-MM-DD')
          : '',
      },
      ...(addItemInfo &&
        talhao.itemSaldo && {
          itemEstoque: {
            id: talhao.itemSaldo.id,
            descricao: talhao.itemSaldo.descricao,
          },
        }),
    };
  });

export const getAduboAttributes = (
  formValue: RecomendacaoSemeaduraFormValues | PropsGetAduboAttributes,
) =>
  formValue.adubos.map((adubo) => {
    const regulagem = RecomendacaoSemeaduraService.getRegulagem({
      espacamento: formValue.espacamento!,
      dose: adubo.dose!,
      distribuicaoRegulagem: formValue.distribuicaoRegulagemAdubo!,
    });

    const totalAdubo = RecomendacaoSemeaduraService.getTotalAdubo({
      area: adubo.area!,
      dose: adubo.dose!,
      porcentagemAreaRemonte: formValue.porcentagemAreaRemonte || 0,
    });

    return {
      insumoId: adubo.adubo!.id,
      insumoTipo: adubo.adubo!.tipo,
      area: adubo.area!,
      dose: adubo.dose!,
      glebaId: adubo.talhao!.talhao!.id,
      unidadeId: adubo.unidade!.id,
      parametros: {
        total: totalAdubo,
        regulagem: regulagem.result,
        regulagemMaxima: regulagem.max,
        regulagemMinima: regulagem.min,
      },
    };
  });

export const validarTalhoes = async (
  propriedadeId: number,
  talhoes: TalhaoRow[],
) => {
  const { glebas } = await PropriedadesService.find(String(propriedadeId));

  const talhoesInvalidos = talhoes.filter(
    ({ talhao }) => !glebas?.find(({ id }) => id === talhao!.id),
  );

  if (!talhoesInvalidos.length) return;

  const detail = chain(talhoesInvalidos)
    .map(({ talhao }) => `${talhao?.id} - ${talhao?.descricao}`)
    .uniq()
    .join(', ');

  throw new TalhaoError(
    `Os seguintes talhões não foram encontrados: ${detail}`,
  );
};

export type PropsGetAduboAttributes = {
  adubos: AdubacaoRow[];
  espacamento: number;
  distribuicaoRegulagemAdubo: number;
  porcentagemAreaRemonte?: number;
};

export type PropsGetCultivaresAttributes = {
  talhoes: TalhaoRow[];
  porcentagemCorrecaoDensidade?: number;
  porcentagemAreaRemonte?: number;
  porcentagemCorrecaoQuantidade?: number;
  sementeCobertura: boolean;
  espacamento?: number;
  distribuicaoRegulagemAdubo?: number;
  atividade?: Atividade;
};

export type PropsGetRecomendacaoPrint = {
  id?: number;
  situacao?: RecomendacaoSemeaduraSituacao;
  status?: string;
  adubos: AdubacaoRow[];
  sementeCobertura: boolean;
  comMargemSeguranca: boolean;
  distribuicaoRegulagemAdubo?: number;
  porcentagemCorrecaoQuantidade?: number;
  espacamento?: number;
  talhoes: TalhaoRow[];
  distribuicaoRegulagemSemente?: number;
  porcentagemCorrecaoDensidade?: number;
  porcentagemAreaRemonte?: number;
  rendimentoOperacionalMaximo?: number;
  rendimentoOperacionalMinimo?: number;
  atividade?: Atividade;
  propriedadeDescricao?: string;
  safraId?: string;
  safra?: Safra;
  recomendante?: string;
  dataHoraImpressao?: string;
};
